import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Pencil, Trash2, UserCheck, UserCog, UserX } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  ROLE_LABELS,
  USER_STATUS_LABELS,
  createSalesRepSchema,
  isConfigurableRole,
  levelsOf,
  nodeOf,
  roleKey,
  rolesBelow,
  type ClonableTier,
  type Paginated,
  type UserDto,
} from "@gatiq/shared";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  FilterSelect,
  Page,
  PageHeader,
  Pagination,
  RowActions,
  SearchInput,
  SelectField,
  SlideOver,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeleton,
  TextInput,
  Toolbar,
  ToolbarCount,
  statusTone,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { ListFilters, useListFilters } from "@/components/ListFilters";
import { useAuth } from "@/auth/AuthProvider";
import { useCan } from "@/auth/access";
import { roleName, useOrgRoles } from "@/auth/useOrgRoles";
import { TempPasswordModal } from "@/components/TempPasswordModal";
import { ChangeRoleModal } from "@/components/ChangeRoleModal";
import { clearFieldError, PhoneField, validateFieldOnBlur, validateForm } from "@/components/FormField";

const PAGE_SIZE = 20;

interface CreateForm {
  name: string;
  email: string;
  phone: string;
  level: number;
  parentId: string;
}

const EMPTY_FORM: CreateForm = { name: "", email: "", phone: "", level: 1, parentId: "" };

/** The clonable tiers. */
type TeamRole = ClonableTier;

const PLURAL: Record<TeamRole, string> = {
  sales_rep: "Sales reps",
  distributor: "Distributors",
  sub_distributor: "Sub-distributors",
};

/**
 * Staff for every clonable tier. When the org stacks a tier into levels
 * (Distributor 1 -> Distributor 2), each person gets a level and, below
 * level 1, the person one level up they report to.
 */
export function SalesRepsPage({ role = "sales_rep" }: { role?: TeamRole }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const label = ROLE_LABELS[role];
  const noun = label.toLowerCase();
  const { user } = useAuth();
  const can = useCan();
  const canCreate = can("team", "create");
  const canEdit = can("team", "edit");
  const canDelete = can("team", "delete");
  // Levels and their names come from the org's roles (D-16): "Super Stockist", not "Distributor 1".
  const roles = useOrgRoles();
  const levels = levelsOf(roles?.tierLevels, role);
  const levelName = (level?: number | null): string => roleName(roles, role, level) ?? `${label} ${level ?? 1}`;
  const nameOfKey = (key: string): string =>
    roles?.roles.find((r) => r.key === key)?.name ?? `${ROLE_LABELS[nodeOf(key).tier as ClonableTier]} ${nodeOf(key).level}`;
  /** Who a level reports to: whichever sales role sits above it in the chain (D-16). */
  const managerOf = (level: number): string | null =>
    level <= 1 ? null : (roles?.roles.find((r) => r.key === roleKey(role, level))?.reportsTo ?? roleKey(role, level - 1));
  // Within their own chain, a person adds only people who would report to them or to someone below them.
  const myKey = user && isConfigurableRole(user.role) ? roleKey(user.role, user.level) : null;
  const reach = myKey ? [myKey, ...rolesBelow(roles?.chain ?? [], myKey)] : [];
  const levelChoices = Array.from({ length: levels }, (_, i) => i + 1).filter((level) => {
    if (canCreate !== "own") return true;
    // A distributor supplies the sub-distributors they sign up, rather than managing a level of them.
    if (level === 1) return role === "sub_distributor" && user?.role === "distributor";
    const manager = managerOf(level);
    return Boolean(manager && reach.includes(manager));
  });
  const [parents, setParents] = useState<UserDto[]>([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const filters = useListFilters(() => setPage(1));
  const [data, setData] = useState<Paginated<UserDto> | null>(null);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<{ name: string; signInWith: string; tempPassword: string } | null>(
    null,
  );

  const [selected, setSelected] = useState<UserDto | null>(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [toDelete, setToDelete] = useState<UserDto | null>(null);
  // D-17: promoting someone to another role is the org admin's alone.
  const [roleTarget, setRoleTarget] = useState<UserDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Who a new person can report to: everyone in the role above their level in the chain.
  const managerKey = managerOf(form.level);
  useEffect(() => {
    if (!managerKey) {
      setParents([]);
      return;
    }
    const manager = nodeOf(managerKey);
    const query = new URLSearchParams({
      role: manager.tier,
      level: String(manager.level),
      page: "1",
      pageSize: "100",
    });
    api
      .get<Paginated<UserDto>>(`/org/sales-reps?${query.toString()}`)
      .then((res) => setParents(res.items))
      .catch(() => setParents([]));
  }, [managerKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ role, page: String(page), pageSize: String(PAGE_SIZE) });
      filters.apply(query);
      if (q.trim()) query.set("q", q.trim());
      const result = await api.get<Paginated<UserDto>>(`/org/sales-reps?${query.toString()}`);
      setData(result);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : `Could not load ${PLURAL[role].toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  }, [page, q, role, filters.apply, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    const validation = validateForm(createSalesRepSchema, {
      name: form.name,
      email: form.email,
      phone: form.phone,
      role,
      level: form.level,
      parentId: form.level > 1 && form.parentId ? form.parentId : undefined,
    });
    if (!validation.success) {
      setFields(validation.fields);
      return;
    }
    setFields({});
    setSaving(true);
    try {
      const result = await api.post<{ user: UserDto; tempPassword: string }>(
        "/org/sales-reps",
        validation.data,
      );
      toast.success(`${result.user.name} added.`);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      setCreated({
        name: result.user.name,
        signInWith: result.user.email ?? result.user.phone ?? "",
        tempPassword: result.tempPassword,
      });
      await load();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setFields(err.fields ?? {});
        toast.error(err.message);
      } else {
        toast.error(`Could not add this ${noun}.`);
      }
    } finally {
      setSaving(false);
    }
  }

  function openDetail(rep: UserDto): void {
    setSelected(rep);
    setEditName(rep.name);
    setEditPhone(rep.phone ?? "");
  }

  async function saveEdit(): Promise<void> {
    if (!selected) return;
    setSavingEdit(true);
    try {
      const updated = await api.patch<UserDto>(`/org/sales-reps/${selected.id}`, {
        name: editName,
        // Older accounts may have no mobile yet; an empty field is left alone, not rejected.
        ...(editPhone.trim() ? { phone: editPhone } : {}),
      });
      toast.success("Saved.");
      setSelected(updated);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not save changes.");
    } finally {
      setSavingEdit(false);
    }
  }

  async function toggleSuspend(rep = selected): Promise<void> {
    if (!rep) return;
    const nextStatus = rep.status === "suspended" ? "active" : "suspended";
    setSavingEdit(true);
    try {
      const updated = await api.patch<UserDto>(`/org/sales-reps/${rep.id}`, { status: nextStatus });
      toast.success(nextStatus === "suspended" ? "Suspended." : "Reactivated.");
      // From the row menu the detail panel is closed — refresh it only if open.
      setSelected((s) => (s?.id === updated.id ? updated : s));
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not update status.");
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.del(`/org/sales-reps/${toDelete.id}`);
      toast.success(`${toDelete.name} removed.`);
      setToDelete(null);
      setSelected(null);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : `Could not remove this ${noun}.`);
    } finally {
      setDeleting(false);
    }
  }

  function openCreate(): void {
    setForm((f) => (levelChoices.includes(f.level) ? f : { ...f, level: levelChoices[0] ?? 1, parentId: "" }));
    setCreateOpen(true);
  }

  // "You" can be who a new person reports to, when they would sit directly under your role.
  const parentOptions = [
    ...(canCreate === "own" && user && myKey === managerKey ? [{ value: user.id, label: `${user.name} (you)` }] : []),
    ...parents.map((p) => ({ value: p.id, label: `${p.name} (${p.displayId ?? ""})` })),
  ];
  // One right answer is not a question: yourself, or the only person who could be the manager.
  const defaultParent =
    parentOptions.length === 1
      ? parentOptions[0]!.value
      : canCreate === "own" && user && myKey === managerKey
        ? user.id
        : "";
  useEffect(() => {
    if (defaultParent && !form.parentId) setForm((f) => ({ ...f, parentId: defaultParent }));
  }, [defaultParent, form.parentId]);
  const addButton =
    canCreate && levelChoices.length > 0 ? <Button onClick={openCreate}>+ Add {noun}</Button> : undefined;

  return (
    <Page bleed>
      <PageHeader
        description={
          levels > 1
            ? `${levels} levels: ${Array.from({ length: levels }, (_, i) => levelName(i + 1)).join(" → ")}`
            : undefined
        }
        title={PLURAL[role]}
      />

      <Card fill>
        <Toolbar
          trailing={
            <>
              {data ? <ToolbarCount count={data.total} noun={noun} /> : null}
              {addButton}
            </>
          }
        >
          <SearchInput
            label={`Search ${PLURAL[role].toLowerCase()}`}
            onChange={(value) => {
              setQ(value);
              setPage(1);
            }}
            placeholder="Search by name, email or phone…"
            value={q}
          />
          <FilterSelect label="Role" value={role} allLabel={null}
            options={(["sales_rep", "distributor", "sub_distributor"] as const)
              .filter((tier) => tier === role || !roles || roles.roles.some((entry) => nodeOf(entry.key).tier === tier))
              .map((tier) => ({ value: tier, label: roleName(roles, tier, 1) ?? ROLE_LABELS[tier] }))}
            onChange={(value) => {
              const routes: Record<string, string> = { sales_rep: "sales-reps", distributor: "distributors", sub_distributor: "sub-distributors" };
              filters.clear(); setQ("");
              navigate(location.pathname.replace(/\/(sales-reps|distributors|sub-distributors)\/?$/, `/${routes[value]}`));
            }} />
          <ListFilters definitions={data?.filters} filters={filters} />
        </Toolbar>

        {loading ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TableSkeleton cols={5} />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState
              action={addButton}
              message={canCreate ? `Add your first ${noun} to start building the network.` : `No ${PLURAL[role].toLowerCase()} are in your network yet.`}
              title={`No ${PLURAL[role].toLowerCase()} yet`}
            />
          </div>
        ) : (
          <Table fill searchable={false} filterable={false}>
            <THead>
              <TR>
                <TH>Name</TH>
                {levels > 1 ? <TH>Level</TH> : null}
                {levels > 1 ? <TH>Reports to</TH> : null}
                <TH>Email</TH>
                <TH>Phone</TH>
                <TH>Status</TH>
                <TH>ID</TH>
                <TH align="right" width="4.5rem">
                  <span className="sr-only">Actions</span>
                </TH>
              </TR>
            </THead>
            <tbody>
              {data.items.map((rep) => (
                <TR key={rep.id} onClick={() => navigate(`${location.pathname}/${rep.id}`)}>
                  <TD><Link className="font-medium text-brand-ink hover:underline" to={`${location.pathname}/${rep.id}`} onClick={(event) => event.stopPropagation()}>{rep.name}</Link></TD>
                  {levels > 1 ? (
                    <TD>
                      <Badge tone="brand">{levelName(rep.level)}</Badge>
                    </TD>
                  ) : null}
                  {levels > 1 ? <TD>{rep.parentName ?? "—"}</TD> : null}
                  <TD>{rep.email ?? "—"}</TD>
                  <TD>{rep.phone}</TD>
                  <TD>
                    <Badge tone={statusTone(rep.status)}>{USER_STATUS_LABELS[rep.status]}</Badge>
                  </TD>
                  <TD className="font-mono text-xs">{rep.displayId}</TD>
                  <TD align="right">
                    <RowActions
                      items={[
                        { label: "View profile", onSelect: () => navigate(`${location.pathname}/${rep.id}`) },
                        { label: "Edit details", icon: Pencil, hidden: !canEdit, onSelect: () => openDetail(rep) },
                        rep.status === "suspended"
                          ? {
                              label: "Reactivate",
                              icon: UserCheck,
                              hidden: !canEdit,
                              onSelect: () => void toggleSuspend(rep),
                            }
                          : { label: "Suspend", icon: UserX, hidden: !canEdit, onSelect: () => void toggleSuspend(rep) },
                        {
                          label: "Change role",
                          icon: UserCog,
                          hidden: user?.role !== "org_admin",
                          onSelect: () => setRoleTarget(rep),
                        },
                        {
                          label: "Delete",
                          icon: Trash2,
                          danger: true,
                          hidden: !canDelete,
                          onSelect: () => setToDelete(rep),
                        },
                      ]}
                      label={rep.name}
                    />
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
        {data ? (
          <div className="shrink-0">
            <Pagination onChange={setPage} page={data.page} total={data.total} totalPages={data.totalPages} />
          </div>
        ) : null}
      </Card>

      <SlideOver
        footer={
          <>
            <Button onClick={() => setCreateOpen(false)} variant="secondary">
              Cancel
            </Button>
            <Button form="create-sales-rep" loading={saving} type="submit">
              Create
            </Button>
          </>
        }
        onClose={() => setCreateOpen(false)}
        open={createOpen}
        title={levels > 1 ? `Add ${levelName(form.level)}` : `Add ${noun}`}
      >
        <form className="flex flex-col gap-4" id="create-sales-rep" noValidate onSubmit={handleCreate}>
          {levelChoices.length > 1 ? (
            <SelectField
              label="Level"
              onChange={(e) => setForm((f) => ({ ...f, level: Number(e.target.value), parentId: "" }))}
              options={levelChoices.map((level) => ({
                value: String(level),
                label: `${levelName(level)}${level === 1 ? " (top)" : ""}`,
              }))}
              value={String(form.level)}
            />
          ) : null}
          {managerKey && parentOptions.length === 1 ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-ink">Reports to</span>
              <p className="flex h-10 items-center rounded-md border border-rule bg-ink/[0.03] px-3 text-sm text-ink">
                {parentOptions[0]!.label}
              </p>
            </div>
          ) : managerKey ? (
            <SelectField
              error={fields.parentId}
              hint={parentOptions.length === 0 ? `Add a ${nameOfKey(managerKey)} first.` : undefined}
              label="Reports to"
              onChange={(e) => {
                setForm((f) => ({ ...f, parentId: e.target.value }));
                clearFieldError(setFields, "parentId");
              }}
              options={parentOptions}
              placeholder={`Choose a ${nameOfKey(managerKey)}`}
              required
              value={form.parentId}
            />
          ) : null}
          <TextInput
            autoComplete="name"
            error={fields.name}
            label="Full name"
            onBlur={() => validateFieldOnBlur(createSalesRepSchema, form, "name", setFields)}
            onChange={(e) => {
              setForm((f) => ({ ...f, name: e.target.value }));
              clearFieldError(setFields, "name");
            }}
            required
            value={form.name}
          />
          <TextInput
            autoComplete="email"
            error={fields.email}
            label="Email"
            onBlur={() => validateFieldOnBlur(createSalesRepSchema, form, "email", setFields)}
            onChange={(e) => {
              setForm((f) => ({ ...f, email: e.target.value }));
              clearFieldError(setFields, "email");
            }}
            type="email"
            value={form.email}
          />
          <PhoneField
            error={fields.phone}
            hint="10-digit mobile number"
            label="Mobile"
            onBlur={() => validateFieldOnBlur(createSalesRepSchema, form, "phone", setFields)}
            onChange={(digits) => {
              setForm((f) => ({ ...f, phone: digits }));
              clearFieldError(setFields, "phone");
            }}
            required
            value={form.phone}
          />
        </form>
      </SlideOver>

      {created ? (
        <TempPasswordModal
          name={created.name}
          signInWith={created.signInWith}
          onClose={() => setCreated(null)}
          open={Boolean(created)}
          tempPassword={created.tempPassword}
        />
      ) : null}

      <SlideOver
        footer={
          selected ? (
            <>
              {canDelete ? (
                <Button onClick={() => setToDelete(selected)} variant="danger">
                  Delete
                </Button>
              ) : null}
              <Button loading={savingEdit} onClick={() => void toggleSuspend()} variant="secondary">
                {selected.status === "suspended" ? "Reactivate" : "Suspend"}
              </Button>
              <Button loading={savingEdit} onClick={() => void saveEdit()}>
                Save
              </Button>
            </>
          ) : null
        }
        onClose={() => setSelected(null)}
        open={Boolean(selected)}
        title={selected?.name ?? ""}
      >
        {selected ? (
          <div className="flex flex-col gap-4">
            <Badge tone={statusTone(selected.status)}>{USER_STATUS_LABELS[selected.status]}</Badge>
            <TextInput label="Full name" onChange={(e) => setEditName(e.target.value)} value={editName} />
            <TextInput label="Mobile" onChange={(e) => setEditPhone(e.target.value)} value={editPhone} />
            <div className="rounded-md border border-rule bg-surface p-3 text-sm text-muted">
              <p>Email: {selected.email ?? "—"}</p>
              <p>ID: {selected.displayId}</p>
              {levels > 1 ? (
                <p>
                  Level: {levelName(selected.level)}
                  {selected.parentName ? ` · reports to ${selected.parentName}` : ""}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </SlideOver>

      <ConfirmDialog
        loading={deleting}
        message={`Remove ${toDelete?.name ?? `this ${noun}`}? Their counters keep running, but need a new ${noun} assigned.`}
        onClose={() => setToDelete(null)}
        onConfirm={() => void handleDelete()}
        open={Boolean(toDelete)}
        title={`Remove ${noun}`}
      />

      <ChangeRoleModal
        onChanged={() => {
          setRoleTarget(null);
          setSelected(null);
          void load();
        }}
        onClose={() => setRoleTarget(null)}
        person={roleTarget}
      />
    </Page>
  );
}
